import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { UserPlus, Wallet, FileText, Building2 } from 'lucide-react';
import Modal from 'components/shared/Modal';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { CircleCheck } from 'lucide-react';

const ONBOARDING_STEPS_DATA = [
  {
    title: 'Sélectionnez une société agréée',
    description:
      'Commencez par sélectionner la société agréée à laquelle vous serez rattaché. Cette étape est essentielle pour accéder à toutes les fonctionnalités du système.',
    elements: ['Liste complète des sociétés agréées', 'Sélection simple et intuitive'],
    icon: <Building2 className="w-12 h-12 bg-hajj-primary text-white p-2 rounded-lg" />,
  },
  {
    title: 'Ajoutez vos pèlerins',
    description:
      'Commencez par enregistrer vos pèlerins dans le système. Cliquez sur le bouton vert en haut à droite pour ajouter un nouveau pèlerin avec toutes ses informations.',
    elements: ['Informations complètes du pèlerin', 'Catégorie souhaitée'],
    icon: <UserPlus className="w-12 h-12 bg-hajj-primary text-white p-2 rounded-lg" />,
  },
  {
    title: 'Générez vos factures',
    description:
      "Sélectionnez un pèlerin pour générer une facture. Cliquez sur les trois petits points pour faire apparaître l'option. 'Générez une facture'",
    elements: ['Génération de factures', 'Suivi du statut des factures'],
    icon: <FileText className="w-12 h-12 bg-hajj-primary text-white p-2 rounded-lg" />,
  },
  {
    title: 'Procédez aux paiements',
    description:
      "Sélectionnez une facture pour procéder au paiement. Cliquez sur les trois petits points pour faire apparaître l'option. 'Payer'",
    elements: ['Paiement en ligne sécurisé', 'Historique des transactions'],
    icon: <Wallet className="w-12 h-12 bg-hajj-primary text-white p-2 rounded-lg" />,
  },
];

const TOTAL_STEPS = ONBOARDING_STEPS_DATA.length;

const OnboardingModal = ({
  isOpened = false,
  isOnboarded = false,
  updateOnboardingStatus = () => {},
  closeModal = () => {},
}) => {
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(0);
  const currentStepData = ONBOARDING_STEPS_DATA[currentOnboardingStep];
  const currentStep = currentOnboardingStep + 1;
  const { title, description, elements, icon } = currentStepData;

  const handleFinish = () => {
    if (!isOnboarded) {
      updateOnboardingStatus();
    }

    closeModal();
  };

  const handleNextStep = () => {
    if (currentOnboardingStep < TOTAL_STEPS - 1) {
      setCurrentOnboardingStep((prev) => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePreviousStep = () => {
    if (currentOnboardingStep > 0) {
      setCurrentOnboardingStep((prev) => prev - 1);
    }
  };

  return (
    <Modal
      isOpen={isOpened}
      icon={icon}
      isUnclosable
      className="w-[90%] lg:w-[400px] lg:h-[500px]"
      onOpenChaPassernge={closeModal}
      title={title}
      footer={
        <div className="flex justify-between gap-2">
          <Button onClick={handlePreviousStep} variant="outline" disabled={currentOnboardingStep <= 0}>
            Précédent
          </Button>
          <Button variant="hajj" onClick={handleNextStep}>
            {TOTAL_STEPS === currentStep ? 'Terminer' : 'Suivant'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 mb-4">
        <p className="text-sm text-gray-700">{description}</p>
        <Card className="flex flex-col gap-3 bg-emerald-50 border-hajj-primary/20 p-3">
          {elements.map((element) => {
            return (
              <div key={element} className="flex gap-2">
                <CircleCheck className="text-hajj-primary w-5 h-5 shrink-0" />
                <p>{element}</p>
              </div>
            );
          })}
        </Card>
      </div>

      <p className="text-xs font-medium text-gray-500 whitespace-nowrap">
        Étape {currentStep} sur {TOTAL_STEPS}
      </p>
    </Modal>
  );
};

OnboardingModal.propTypes = {
  isOpened: PropTypes.bool,
  isOnboarded: PropTypes.bool,
  closeModal: PropTypes.func,
  updateOnboardingStatus: PropTypes.func,
};

export default OnboardingModal;
